/**
 * The user's allowed pages and tours for the help assistant (D10-ACT, D10-SEC-003).
 *
 * `allowedRoutes` are the navigation commands -- the same visibility predicate
 * the sidebar and the Cmd/Ctrl-K palette use -- plus the user's own landing page
 * when it has no nav entry (#3624). `allowedTours` are the registered tours the
 * engine would actually offer this user. Both come from `lib/support/lists`,
 * the same functions the knowledge-pack catalogue test exports per persona, so
 * the server's catalogue always knows every value sent. Both are sent with each
 * chat turn as data and gate what the panel itself will link to or launch.
 */
import { useMemo } from "react";
import { useAuth, useEffectiveAuth } from "../../contexts/auth";
import { useCommandRegistry } from "../../lib/commands/useCommandRegistry";
import { supportAllowedRoutes, supportAllowedTours } from "../../lib/support/lists";
import { welcomeTourIdForUser } from "../../lib/tours/registry";
import { useRouteTourId, useTourGate } from "../../lib/tours/useTourGate";
import { landingPathForUser } from "../auth/ProtectedRoute";

export interface SupportLists {
  allowedRoutes: string[];
  allowedTours: string[];
  /** The tour "Take a tour" starts: this page's tour, else the user's welcome tour. */
  quickTourId: string | null;
}

export function useSupportLists(): SupportLists {
  const { commands } = useCommandRegistry();
  const gate = useTourGate();
  const { user } = useAuth();
  const { role, preset } = useEffectiveAuth();
  const routeTourId = useRouteTourId();

  const allowedRoutes = useMemo(
    () => supportAllowedRoutes(commands, landingPathForUser(role, preset)),
    [commands, role, preset],
  );
  const allowedTours = useMemo(() => supportAllowedTours(gate), [gate]);
  const quickTourId = useMemo(() => {
    if (routeTourId) return routeTourId;
    const welcome = welcomeTourIdForUser(user?.role, user?.permission_preset);
    return allowedTours.includes(welcome) ? welcome : null;
  }, [routeTourId, user?.role, user?.permission_preset, allowedTours]);

  return { allowedRoutes, allowedTours, quickTourId };
}
